import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import StatusBadge from './StatusBadge.jsx';
import GroupBadge from './GroupBadge.jsx';
import DeviceCell from './DeviceCell.jsx';
import FilterPopover from './FilterPopover.jsx';
import EmptyState from './EmptyState.jsx';
import { AREA_TYPES } from '../data/mockData.js';

/**
 * Formats a datetime ISO string to relative time ("3 hours ago", "2 days ago").
 * Falls back to locale string for dates > 30 days.
 */
function relativeTime(isoString) {
  if (!isoString) return '—';
  const now = new Date();
  const then = new Date(isoString);
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return then.toLocaleDateString();
}

function fullDateTime(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString();
}

// ---------------------------------------------------------------------------
// Status popover — wraps a cell value and shows detail on hover
// ---------------------------------------------------------------------------
const StatusPopover = ({ children, message }) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);
  const hoverTimeout = useRef(null);

  if (!message) return children;

  const handleEnter = () => {
    clearTimeout(hoverTimeout.current);
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popoverW = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    let left = centerX - popoverW / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));

    setStyle({
      position: 'fixed',
      zIndex: 9999,
      left: `${left}px`,
      top: spaceBelow < 120 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 120 ? `${window.innerHeight - rect.top + 6}px` : undefined,
      width: `${popoverW}px`,
      whiteSpace: 'normal',
    });
    setOpen(true);
  };

  const handleLeave = () => {
    hoverTimeout.current = setTimeout(() => setOpen(false), 100);
  };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  return (
    <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={handleLeave} style={{ position: 'relative', display: 'inline-block' }}>
      {children}
      {open ? (
        <span className="na-type-popover" style={style}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{message}</span>
        </span>
      ) : null}
    </span>
  );
};

/**
 * ResidentGroupsCell — inline pill tags for each group name.
 */
const ResidentGroupsCell = ({ groups }) => {
  if (!groups || groups.length === 0) {
    return <span className="rp-cell-text--muted">&mdash;</span>;
  }

  return (
    <div className="rp-group-pills">
      {groups.map((g, i) => (
        <span key={g.resident_group_id || i} className="rp-group-pill">
          {g.resident_group_name || g}
        </span>
      ))}
    </div>
  );
};

/**
 * Column definitions for the residents table.
 * Each column has: key, label, sortable, filterable, required (can't hide), render fn.
 */
export const COLUMN_DEFS = [
  { key: 'resident_first_name', label: 'First Name', sortable: true, filterable: true, required: true },
  { key: 'resident_last_name', label: 'Last Name', sortable: true, filterable: true, required: true },
  { key: 'resident_nickname', label: 'Nickname', sortable: true, filterable: true, required: false },
  { key: 'area_name', label: 'Area', sortable: true, filterable: true, required: true },
  { key: 'area_type_name', label: 'Area Type', sortable: true, filterable: true, required: false },
  // Gated column — only surfaced by ResidentsPage when an active RTLS vendor
  // (vendor_id 14) is configured. Location data is mapped onto the resident row
  // from the Inovonics locations fetch (by device serial).
  { key: 'locating', label: 'Real-time Locating', sortable: true, filterable: false, required: false },
  { key: 'resident_groups', label: 'Groups', sortable: false, filterable: true, required: false },
  { key: 'resident_phone', label: 'Phone', sortable: true, filterable: false, required: false },
  { key: 'resident_email', label: 'Email', sortable: true, filterable: false, required: false },
  { key: 'ehr_level_of_care', label: 'Care Level', sortable: true, filterable: true, required: false },
  { key: 'devices', label: 'Devices', sortable: false, filterable: true, required: false },
  { key: 'check_in_time', label: 'Last Check-in', sortable: true, filterable: false, required: false },
  { key: 'status', label: 'Status', sortable: true, filterable: true, required: true },
];

/**
 * ResidentTable — full data table with sorting, filtering, sticky header,
 * row selection, row actions, and consistent row heights.
 */
const ResidentTable = ({
  data,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onEditResident,
  onManageDevices,
  onMoveOut,
  onRelocate,
  onAddResident,
  onClearFilters,
  allData,
}) => {
  const activeColumns = useMemo(
    () => COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
    [visibleColumns]
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = useCallback(
    (columnKey) => {
      const values = new Set();
      (allData || data).forEach((row) => {
        if (columnKey === 'resident_groups') {
          const groups = row.resident_groups || [];
          if (groups.length === 0) {
            values.add('(empty)');
          } else {
            groups.forEach((g) => values.add(g.resident_group_name));
          }
        } else if (columnKey === 'devices') {
          // Filter on device_name, matching what the Devices popover lists.
          const devs = row.devices || [];
          if (devs.length === 0) {
            values.add('(empty)');
          } else {
            devs.forEach((d) => { if (d.device_name) values.add(d.device_name); });
          }
        } else if (columnKey === 'status') {
          values.add(row.status || (row.is_active ? 'Active' : 'Moved Out'));
        } else {
          const val = row[columnKey];
          values.add(val != null && val !== '' ? String(val) : '(empty)');
        }
      });
      return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    },
    [allData, data]
  );

  const allSelected = data.length > 0 && data.every((r) => selectedIds.includes(r.resident_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg className="rp-icon-sm rp-sort-icon rp-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg className="rp-icon-sm rp-sort-icon rp-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
      </svg>
    ) : (
      <svg className="rp-icon-sm rp-sort-icon rp-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'resident_first_name':
        return (
          <Link to={`/community/residents/${row.resident_id}`} className="rp-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', textDecoration: 'none' }}>
            {row.resident_first_name || '—'}
          </Link>
        );

      case 'resident_last_name':
        return (
          <Link to={`/community/residents/${row.resident_id}`} className="rp-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', textDecoration: 'none' }}>
            {row.resident_last_name || '—'}
          </Link>
        );

      case 'area_type_name':
        return (
          <span
            className="rp-area-type-tag"
            title={AREA_TYPES[row.area_type_name] || row.area_type_description || row.area_type_name}
          >
            {row.area_type_name}
          </span>
        );

      case 'resident_groups':
        return <ResidentGroupsCell groups={row.resident_groups || []} />;

      case 'devices':
        return <DeviceCell devices={row.devices || []} onClick={() => onManageDevices?.(row)} />;

      case 'check_in_time':
        return (
          <StatusPopover message={fullDateTime(row.check_in_time)}>
            <span className={`rp-cell-text rp-cell-text--secondary ${row?.check_in_time ? "has-popover" : ""}`}>
              {relativeTime(row.check_in_time)}
            </span>
          </StatusPopover>
        );

      case 'status':
        return <StatusBadge status={row.status || (row.is_active ? 'Active' : 'Moved Out')} />;

      case 'locating': {
        // RTLS location mapped onto the resident (by device serial) in
        // ResidentsPage. row.location = { area_name, area_id, timestamp }; the
        // area name links to its AreaProfilePage when an area_id is present, and
        // hovering shows the (campus-local) timestamp of the reading.
        const loc = row.location;
        if (!loc?.area_name) return <span className="rp-cell-text rp-cell-text--secondary">—</span>;
        const areaEl = loc.area_id ? (
          <Link to={`/community/areas/${loc.area_id}`} className="rp-cell-text rp-cell-text--link" style={{ textDecoration: 'none' }}>
            {loc.area_name}
          </Link>
        ) : (
          <span className="rp-cell-text">{loc.area_name}</span>
        );
        return loc.timestamp
          ? <StatusPopover message={`Last update: ${loc.timestamp}`}>{areaEl}</StatusPopover>
          : areaEl;
      }

      case 'resident_phone':
        return <span className="rp-cell-text rp-cell-text--nowrap">{row.resident_phone || '—'}</span>;

      case 'resident_email':
        return <span className="rp-cell-text rp-cell-text--truncate">{row.resident_email || '—'}</span>;

      case 'ehr_level_of_care':
        return <span className="rp-cell-text">{row.ehr_level_of_care || '—'}</span>;

      case 'area_name': {
        // Quick-relocate shortcut sits next to the area so a move can be
        // started without opening the kebab. Same icon as the kebab item so
        // the affordance is learnable. Only for active residents — a moved-out
        // resident has no room to move out of.
        const canRelocate = !!row.is_active && typeof onRelocate === 'function';
        return (
          <span className="rp-area-cell">
            {row.area_id ? (
              <Link to={`/community/areas/${row.area_id}`} className="rp-cell-text rp-cell-text--link" style={{ textDecoration: 'none' }}>
                {row.area_name}
              </Link>
            ) : (
              <span className="rp-cell-text">{row.area_name || '—'}</span>
            )}
            {canRelocate ? (
              <button
                type="button"
                className="rp-relocate-btn"
                /* Anchor for the interactive help guide — see
                   docs/INTERACTIVE_HELP_PLAN.md §5.2. One match per row; the
                   guide spotlights the first viewport-visible one. */
                data-help-id="residents.row-relocate"
                onClick={(e) => { e.stopPropagation(); onRelocate(row); }}
                title={`Relocate ${row.resident_first_name || ''} ${row.resident_last_name || ''}`.trim()}
                aria-label={`Relocate ${row.resident_first_name || ''} ${row.resident_last_name || ''}`.trim()}
              >
                <RelocateIcon className="rp-icon-sm" />
              </button>
            ) : null}
          </span>
        );
      }

      default:
        return <span className="rp-cell-text">{row[columnKey] || '—'}</span>;
    }
  };

  if (data.length === 0) {
    const hasAnyData = (allData || []).length > 0;
    return (
      <EmptyState
        type={hasAnyData ? 'no-results' : 'no-data'}
        onAddResident={hasAnyData ? undefined : onAddResident}
        onClearFilters={hasAnyData ? onClearFilters : undefined}
      />
    );
  }

  return (
    <div className="rp-table-wrap">
      <table className="rp-table">
        <thead className="rp-thead">
          <tr>
            {/* Checkbox column */}
            <th scope="col" className="rp-th rp-th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={onSelectAll}
                className="rp-checkbox"
                aria-label="Select all residents"
              />
            </th>

            {/* Actions column */}
            <th scope="col" className="rp-th rp-th--actions">Edit</th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="rp-th">
                <div className="rp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="rp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable && (
                    <FilterPopover
                      columnKey={col.key}
                      label={col.label}
                      values={getFilterValues(col.key)}
                      selectedValues={filters[col.key] || []}
                      onChange={onFilterChange}
                    />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="rp-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.resident_id);
            return (
              <tr
                key={row.resident_id}
                className={`rp-tr ${isSelected ? 'is-selected' : ''}`}
              >
                {/* Checkbox */}
                <td className="rp-td rp-td--checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelectRow(row.resident_id)}
                    className="rp-checkbox"
                    aria-label={`Select ${row.resident_first_name} ${row.resident_last_name}`}
                  />
                </td>

                {/* Row actions kebab */}
                <td className="rp-td rp-td--actions">
                  <RowActionsMenu
                    onRelocate={onRelocate}
                    resident={row}
                    onEdit={onEditResident}
                    onManageDevices={onManageDevices}
                    onMoveOut={onMoveOut}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="rp-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * RelocateIcon — bidirectional transfer arrows, used for the Relocate action in
 * both the kebab menu and the Area column shortcut so the two read as the same
 * action.
 */
const RelocateIcon = ({ className = 'rp-icon-md rp-row-actions__item-icon' }) => (
  <svg style={{width: "1rem", height: "1rem"}} className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.75}
       stroke="currentColor" aria-hidden="true">
    {/* Arrow entering a doorway — "move into a new room". Note this is the
        mirror of the Move Out icon in this same kebab; the two are deliberately
        distinguished only by arrow direction, so keep their labels adjacent and
        clear. */}
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 9V5.25A2.25 2.25 0 0110.5 3h6a2.25 2.25 0 012.25 2.25v13.5A2.25 2.25 0 0116.5 21h-6a2.25 2.25 0 01-2.25-2.25V15M12 9l3 3m0 0l-3 3m3-3H2.25" />
  </svg>
);

/**
 * RowActionsMenu — kebab menu for per-row actions.
 */
const RowActionsMenu = ({ resident, onEdit, onManageDevices, onMoveOut, onRelocate }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

    const handleToggle = () => {
        if (!open && menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 180) {
                setMenuStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, right: `${window.innerWidth - rect.right}px`, top: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            } else {
                setMenuStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, right: `${window.innerWidth - rect.right}px`, bottom: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            }
        }
        setOpen(!open);
    };

  return (
    <div className="rp-row-actions" ref={menuRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="rp-row-actions__trigger"
        aria-label={`Actions for ${resident.resident_first_name} ${resident.resident_last_name}`}
        aria-expanded={open}
        /* Help-guide anchor (docs/INTERACTIVE_HELP_PLAN.md §5.2). One per row;
           the guide spotlights the first viewport-visible one. */
        data-help-id="residents.row-actions"
      >
        <i className="fe fe-more-vertical" aria-hidden="true" />
      </button>

      {open && (
        <div className="rp-row-actions__menu" style={menuStyle}>
          <Link
            to={`/community/residents/${resident.resident_id}`}
            className="rp-row-actions__item"
            style={{ textDecoration: 'none' }}
            onClick={() => setOpen(false)}
          >
            <svg className="rp-icon-md rp-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            View Profile
          </Link>
          {/* Absent when the campus's EHR owns resident records (the page
              withholds the handler). Same guard style as Relocate below. */}
          {typeof onEdit === 'function' ? (
            <button
              type="button"
              onClick={() => { onEdit(resident); setOpen(false); }}
              className="rp-row-actions__item"
            >
              <svg className="rp-icon-md rp-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
              </svg>
              Edit Resident
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => { onManageDevices(resident); setOpen(false); }}
            data-help-id="row-actions.manage-devices"
            className="rp-row-actions__item"
          >
            <i className={"fe fe-tablet text-gray-600"}></i>
            Manage Devices
          </button>
          {/* Relocate — active residents only; a moved-out resident has no
              room to move out of. */}
          {resident.is_active && typeof onRelocate === 'function' ? (
            <button
              type="button"
              onClick={() => { onRelocate(resident); setOpen(false); }}
              className="rp-row-actions__item"
            >
              <RelocateIcon />
              Relocate
            </button>
          ) : null}
          {typeof onMoveOut === 'function' ? (
            <button
              type="button"
              onClick={() => { onMoveOut(resident); setOpen(false); }}
              className="rp-row-actions__item rp-row-actions__item--danger"
              /* Help-guide anchor. Only exists while the menu is open, which is
                 what makes it a usable completion signal for the step before. */
              data-help-id="row-actions.move-out"
            >
              <svg className="rp-icon-md rp-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
              Move Out
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ResidentTable;
